/*
 * Brand Templates — hands the fetched org list from the task pane to the
 * Asset Library's Office Dialog window.
 *
 * The dialog has no AuthStateProvider (so no bearer token for the org fetch)
 * and localStorage is the only way to pass data into a window opened via
 * displayDialogAsync.
 *
 * Only the org-managed list travels here — the Asset Library's Brand
 * Templates tab shows the organisation's templates only, not My Templates
 * (those stay where they were saved: Apply Brand's own Saved Templates tab).
 */

import { getErrorMessage } from "../../shared/services/connectorErrors";
import { logSavedBrandTemplatesError, type SavedBrandTemplate } from "../../apply-brand/services/brandTemplatesApi";

const HANDOFF_KEY = "aiva_deckorate_brand_templates_handoff";
const HANDOFF_VERSION = 2;

interface HandoffPayload {
  readonly v: number;
  readonly org: readonly SavedBrandTemplate[];
}

function getStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function writeBrandTemplatesHandoff(org: readonly SavedBrandTemplate[]): boolean {
  const storage = getStorage();
  if (!storage) {
    logSavedBrandTemplatesError("brand-templates-handoff-storage-unavailable", "localStorage is not accessible");
    return false;
  }
  const payload: HandoffPayload = { v: HANDOFF_VERSION, org };
  try {
    storage.setItem(HANDOFF_KEY, JSON.stringify(payload));
    return true;
  } catch (error) {
    logSavedBrandTemplatesError("brand-templates-handoff-write", getErrorMessage(error, "Couldn't stage the brand template list for the Asset Library window."));
    return false;
  }
}

function toValidSavedBrandTemplate(raw: unknown): SavedBrandTemplate | null {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw as Record<string, unknown>;
  if (typeof entry.id !== "string" || !entry.id) return null;
  if (typeof entry.name !== "string" || !entry.name) return null;
  return {
    id: entry.id,
    name: entry.name,
    brand: typeof entry.brand === "string" ? entry.brand : null,
    colors: Array.isArray(entry.colors) ? entry.colors.filter((c): c is string => typeof c === "string") : null,
    themeColors: typeof entry.themeColors === "object" && entry.themeColors !== null ? (entry.themeColors as SavedBrandTemplate["themeColors"]) : null,
    sourceFileUrl: typeof entry.sourceFileUrl === "string" ? entry.sourceFileUrl : null,
    sourceFileUrlCandidates: Array.isArray(entry.sourceFileUrlCandidates)
      ? entry.sourceFileUrlCandidates.filter((c): c is string => typeof c === "string")
      : [],
    aspectRatio: typeof entry.aspectRatio === "string" ? entry.aspectRatio : null,
  };
}

/** Reads the staged org list inside the dialog window. Returns null when
 *  nothing was staged (or it's unreadable/stale). */
export function readBrandTemplatesHandoff(): readonly SavedBrandTemplate[] | null {
  const storage = getStorage();
  if (!storage) return null;

  let raw: string | null;
  try {
    raw = storage.getItem(HANDOFF_KEY);
  } catch (error) {
    logSavedBrandTemplatesError("brand-templates-handoff-read", getErrorMessage(error, "localStorage read failed"));
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    logSavedBrandTemplatesError("brand-templates-handoff-parse", getErrorMessage(error, "Hand-off JSON.parse failed"));
    return null;
  }

  const payload = parsed as Partial<HandoffPayload> | null;
  if (payload?.v !== HANDOFF_VERSION || !Array.isArray(payload.org)) {
    logSavedBrandTemplatesError(
      "brand-templates-handoff-shape",
      `Hand-off payload was version ${String(payload?.v)} with a missing/invalid org array`,
    );
    return null;
  }

  return payload.org.map(toValidSavedBrandTemplate).filter((t): t is SavedBrandTemplate => t !== null);
}

/** Drops the staged list. Called as soon as the dialog closes so a stale
 *  list can't be shown by a later, unrelated dialog open. */
export function clearBrandTemplatesHandoff(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(HANDOFF_KEY);
  } catch (error) {
    logSavedBrandTemplatesError("brand-templates-handoff-clear", getErrorMessage(error, "localStorage remove failed"));
  }
}
